import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { submitForm } from '../utils/submitForm';
import { trackEvent } from '../utils/analytics';

const QuoteModal = ({ isOpen, onClose, serviceName, planName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    requirements: ''
  });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (isOpen) setStatus('idle');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const result = await submitForm(`Quote request: ${serviceName}${planName ? ` (${planName})` : ''}`, {
        ...formData,
        service: serviceName,
        plan: planName
      });
      if (result === 'sent') trackEvent('generate_lead', { form_name: 'quote', service: serviceName, plan: planName || 'none' });
      setStatus(result);
      setFormData({ name: '', email: '', requirements: '' });
    } catch (error) {
      setStatus('error');
    }
  };

  const isDone = status === 'sent' || status === 'mailto';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-modal-title"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="quote-modal-title">
                Get a Quote for {serviceName}{planName ? ` – ${planName}` : ''}
              </h3>
              <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
            </div>

            {isDone ? (
              <div className="form-status success" role="status">
                {status === 'sent'
                  ? 'Thank you! Your quote request has been sent. We will contact you within one business day.'
                  : 'Your email app has opened with your request. Just hit send.'}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="quote-form">
                <div className="form-group">
                  <label htmlFor="quote-name">Name</label>
                  <input
                    id="quote-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quote-email">Email</label>
                  <input
                    id="quote-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quote-requirements">Requirements</label>
                  <textarea
                    id="quote-requirements"
                    value={formData.requirements}
                    onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                    rows="4"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn" disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending...' : 'Submit Quote Request'}
                </button>
                <p className="form-note">
                  By submitting you agree to our <Link to="/privacy" onClick={onClose}>privacy policy</Link>.
                </p>

                {status === 'error' && (
                  <p className="form-status error" role="alert">
                    Something went wrong. Please email us at info@fedigmaforge.com.
                  </p>
                )}
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
